import { Suspense, useCallback, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { Sidebar } from "./Sidebar";
import { MobileSidebar } from "./MobileSidebar";
import { TopBar } from "./TopBar";
import { AmbientBackground } from "./AmbientBackground";
import { Spinner } from "../ui/Spinner";
import { useNotificationSSE } from "../../hooks/useNotificationSSE";
import { ChatSocketProvider } from "../../contexts/ChatSocketProvider";
import { PersistentChatPanel } from "../chat/PersistentChatPanel";
import { RequirePermission } from "../common/RequirePermission";
import { Permissions } from "../../types/permissions";

/**
 * App frame (v3 shell): the document scrolls, the glass header and sidebar
 * stay put. Pages are plain flow content; only /chat sizes itself to the
 * viewport.
 */
export function AppLayout() {
  useNotificationSSE();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { pathname } = useLocation();

  // Next resets the scroll position on navigation; react-router doesn't.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const handleMobileSidebarClose = useCallback(() => {
    setMobileSidebarOpen(false);
  }, []);

  const handleMobileMenuOpen = useCallback(() => {
    setMobileSidebarOpen(true);
  }, []);

  return (
    <ChatSocketProvider>
      <div className="relative flex min-h-screen flex-col bg-canvas text-ink">
        <AmbientBackground />
        <TopBar onMobileMenuOpen={handleMobileMenuOpen} />
        <MobileSidebar
          open={mobileSidebarOpen}
          onClose={handleMobileSidebarClose}
        />
        <div className="relative z-10 mx-auto flex w-full max-w-[1512px] flex-1 gap-5 px-3 py-4 sm:px-5 lg:px-6">
          <Sidebar />
          <main className="flex min-w-0 flex-1 flex-col">
            <Suspense
              fallback={
                <div
                  className="flex min-h-[60vh] items-center justify-center"
                  role="status"
                >
                  <Spinner size={32} />
                  <span className="sr-only">Loading page</span>
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </main>
        </div>
        <RequirePermission permission={Permissions.AI_CHAT}>
          <PersistentChatPanel />
        </RequirePermission>
      </div>
    </ChatSocketProvider>
  );
}
